import {createAsyncThunk} from '@reduxjs/toolkit';
import {get, post, postForm} from '../utils/requests';
import {SWFT_DIGITAL_API_HOST} from '../constants';
import {
    DELETE_MEDIA_FILE_ENDPOINT,
    DELETE_MEDIA_FILE_IMAGE_ENDPOINT,
    GET_MEDIA_ENDPOINT,
    ORGANIZATION_ENDPOINT,
    SAVE_MEDIA_FILE_IMAGE_ENDPOINT,
    UPLOAD_MEDIA_ENDPOINT,
} from 'src/constants/endpoints';
import {
    mediaAllPaginatedFiles,
    mediaFileBulkUpload,
    mediaFileClear,
    mediaFileDeleteFailed,
    mediaFileDeleteInit,
    mediaFileDeleteSuccess,
    mediaFileError,
    mediaFileImageDeleteFailed,
    mediaFileImageDeleteInit,
    mediaFileImageDeleteSuccess,
    mediaFileImageSaveFailed,
    mediaFileImageSaveInit,
    mediaFileImageSaveSuccess,
    mediaFileListClear,
    mediaFileSuccess,
    mediaListFileError,
    mediaListFilesInit,
    mediaListFilesSuccess,
    mediaSampleFetchSuccess,
    mediaSampleFileError,
    mediaSearchFilesError,
    mediaSearchFilesInit,
    mediaSearchFilesSuccess,
    mediaUnsplashError,
    mediaUnsplashSuccess,
    MediaFile,
} from 'src/reducers/media';
import Tracker from 'src/lib/analytics/tracker';
import {ErrorTags, EventNames, EventProperty} from 'src/lib/analytics/events';
import {urlOperation} from '../utils/media';

interface SearchFilesRequest {
    orgUid: string;
    q: string;
    offset: number;
    limit: number;
    folder?: string;
}

export interface UploadRequest {
    file: File;
    fileName?: string;
    orgUid: string;
    uploaded_type: string;
    folder?: string;
}

interface DeleteMediaFileRequest {
    orgUid: string;
    mediaFileUid: string;
}

interface SaveMediaImageFileRequest {
    orgUid: string;
    mediaFileUid: string;
    imageUrl: string;
}

interface DeleteMediaImageFileRequest {
    orgUid: string;
    mediaFileUid: string;
    imageUid: string;
}

const uploadFile = createAsyncThunk('media/uploadFile', async ({params}: {params: UploadRequest}, thunkApi) => {
    const apiEndPoint = SWFT_DIGITAL_API_HOST + UPLOAD_MEDIA_ENDPOINT;
    try {
        thunkApi.dispatch(
            mediaFileBulkUpload({
                loading: true,
                success: false,
                error: '',
                fileName: params.file.name,
            }),
        );
        const formData = new FormData();
        formData.append('file', params.file);
        if (params.folder) {
            formData.append('folder', params.folder);
        }
        formData.append('name', params.fileName ? params.fileName : params.file.name);
        formData.append('size', params.file.size.toString());
        formData.append('type', params.file.type);
        formData.append('org_uid', params.orgUid);
        formData.append('uploaded_type', params.uploaded_type);
        const response = await postForm(apiEndPoint, formData);
        Tracker.trackEvent(EventNames.FILE_UPLOADED, {
            [EventProperty.FileName]: params.file.name,
            [EventProperty.FileSize]: params.file.size.toString(),
            [EventProperty.FileType]: params.file.type,
        });
        thunkApi.dispatch(
            mediaFileBulkUpload({
                loading: false,
                success: true,
                error: '',
                fileName: params.file.name,
            }),
        );
        return response;
    } catch (e: any) {
        thunkApi.dispatch(
            mediaFileBulkUpload({
                loading: false,
                success: false,
                error: e.message,
                fileName: params.file.name,
            }),
        );
        Tracker.trackError(e, ErrorTags.FILE_UPLOAD_ERROR, {
            fileName: params.file.name,
            fileType: params.file.type,
            fileSize: params.file.size.toString(),
            orgUid: params.orgUid,
            uploadedType: params.uploaded_type,
            folder: params.folder,
            apiEndpoint: apiEndPoint,
        });
        throw {failure: 'true'};
    }
});

function extractImageList(imageList: any) {
    return imageList.files.map((file: any) => {
        let savedImages: any = [];
        Object.keys(file.saved_images).forEach((value, index) => {
            const saved_image = file.saved_images[value];
            savedImages.push({
                uid: saved_image.uid,
                cdn_url: saved_image.url,
                thumbnail_url: saved_image.url,
                title: saved_image.title,
                width: saved_image.width,
                height: saved_image.height,
                type: saved_image.type,
                file_size: saved_image.size,
                created_at: saved_image.created_at,
                parent_uid: saved_image.parent_uid,
            });
        });
        return {
            title: file.name,
            cdn_url: file.cdn_url,
            thumbnail_url: file.thumbnail_url,
            file_size: file.file_size,
            file_uid: file.uid,
            height: file.height,
            width: file.width,
            type: file.type,
            subRows: savedImages,
        };
    });
}

const searchMediaFiles = createAsyncThunk('media/search/files', async (param: SearchFilesRequest, thunkApi) => {
    const {orgUid, limit, offset, q, folder} = param;
    try {
        thunkApi.dispatch(mediaSearchFilesInit());
        let url = SWFT_DIGITAL_API_HOST + `/v1/media?org_uid=${orgUid}&query=${q}&limit=${limit}&offset=${offset}`;
        if (folder) {
            url += `&folder_name=${folder}`;
        }
        const imageListV2 = await get(url);
        const newImageList = extractImageList({files: imageListV2.results});
        thunkApi.dispatch(
            mediaSearchFilesSuccess({
                files: newImageList,
                totalResults: imageListV2.count,
            }),
        );
        return newImageList;
    } catch (e: any) {
        console.error('Error searching.', e);
        Tracker.trackError(e, ErrorTags.MEDIA_SEARCH_ERROR, {
            orgUid: param.orgUid,
        });
        thunkApi.dispatch(mediaSearchFilesError());
    }
});

const getMediaFiles = createAsyncThunk(
    'media/listFiles',
    async (
        params: {
            orgUid: string;
            folder?: string;
            limit: number;
            offset: number;
        },
        thunkApi,
    ) => {
        try {
            const {orgUid, folder, offset, limit} = params;
            thunkApi.dispatch(mediaListFilesInit());
            const search =
                '?' +
                new URLSearchParams({
                    folder: folder ?? '',
                }).toString();
            const imageList = await get(SWFT_DIGITAL_API_HOST + ORGANIZATION_ENDPOINT + orgUid + '/files/' + search);

            let url = `${SWFT_DIGITAL_API_HOST}/v1/media?org_uid=${orgUid}&limit=${limit}&offset=${offset}`;
            if (folder) {
                url += `&folder_name=${folder}`;
            }
            const imageListV2 = await get(url);
            const newImageList = extractImageList({files: imageListV2.results});
            thunkApi.dispatch(
                mediaListFilesSuccess({
                    files: newImageList,
                    folders: imageList.folders,
                    totalResults: imageListV2.count,
                }),
            );
            return newImageList;
        } catch (e: any) {
            const {orgUid, folder} = params;
            thunkApi.dispatch(mediaListFileError(e));
            Tracker.trackError(e, ErrorTags.ORG_FILES_FETCH_ERROR, {
                orgUid,
                folder: folder,
            });
            throw {failure: 'true'};
        }
    },
);

const deleteMediaFile = createAsyncThunk('media/deleteMediaFile', async (params: DeleteMediaFileRequest, thunkApi) => {
    try {
        thunkApi.dispatch(mediaFileDeleteInit());
        const body = {
            file_uid: params.mediaFileUid,
            org_uid: params.orgUid,
        };
        const response = await post(SWFT_DIGITAL_API_HOST + DELETE_MEDIA_FILE_ENDPOINT, body);
        Tracker.trackEvent(EventNames.FILE_DELETED, {
            [EventProperty.FileUid]: params.mediaFileUid,
        });

        thunkApi.dispatch(mediaFileDeleteSuccess(response));
        return response;
    } catch (e: any) {
        Tracker.trackError(e, ErrorTags.DELETE_MEDIA_ERROR, {
            orgUid: params.orgUid,
            fileUid: params.mediaFileUid,
        });
        thunkApi.dispatch(mediaFileDeleteFailed(e));
        throw {failure: 'true'};
    }
});

const saveImageMediaFile = createAsyncThunk(
    'media/saveImageMediaFile',
    async (params: SaveMediaImageFileRequest, thunkApi) => {
        try {
            thunkApi.dispatch(mediaFileImageSaveInit());
            const body = {
                media_file_uid: params.mediaFileUid,
                org_uid: params.orgUid,
                url: params.imageUrl,
            };
            const response = await post(SWFT_DIGITAL_API_HOST + SAVE_MEDIA_FILE_IMAGE_ENDPOINT, body);
            Tracker.trackEvent(EventNames.TRANSFORMATION_SAVED, {
                [EventProperty.TransformationFileUid]: response.media_image_uid,
                [EventProperty.FileUid]: response.media_file_uid,
                [EventProperty.TransformationFileUrl]: response.url,
                [EventProperty.FileUrl]: params.imageUrl,
            });

            thunkApi.dispatch(mediaFileImageSaveSuccess(response));
            return response;
        } catch (e: any) {
            Tracker.trackError(e, ErrorTags.SAVE_TRANSFORMATION_ERROR, {
                orgUid: params.orgUid,
                fileUid: params.mediaFileUid,
                fileUrl: params.imageUrl,
            });
            thunkApi.dispatch(mediaFileImageSaveFailed(e));
            throw {failure: 'true'};
        }
    },
);

const deleteImageMediaFile = createAsyncThunk(
    'media/deleteImageMediaFile',
    async (params: DeleteMediaImageFileRequest, thunkApi) => {
        try {
            thunkApi.dispatch(mediaFileImageDeleteInit());

            const body = {
                media_file_uid: params.mediaFileUid,
                org_uid: params.orgUid,
                media_image_uid: params.imageUid,
            };

            const response = await post(SWFT_DIGITAL_API_HOST + DELETE_MEDIA_FILE_IMAGE_ENDPOINT, body);
            Tracker.trackEvent(EventNames.TRANSFORMATION_DELETED);
            thunkApi.dispatch(mediaFileImageDeleteSuccess(response));
            return response;
        } catch (e: any) {
            Tracker.trackError(e, ErrorTags.DELETE_TRANSFORMATION_ERROR, {
                orgUid: params.orgUid,
                fileUid: params.mediaFileUid,
                childUid: params.imageUid,
            });
            thunkApi.dispatch(mediaFileImageDeleteFailed(e));
            throw {failure: 'true'};
        }
    },
);

const getFileMetaData = createAsyncThunk(
    'media/file',
    async ({orgUid, fileUid}: {orgUid: string; fileUid: string}, thunkApi) => {
        try {
            thunkApi.dispatch(mediaListFilesInit());
            const imageMetadata = await get(
                SWFT_DIGITAL_API_HOST + GET_MEDIA_ENDPOINT + '?org_uid=' + orgUid + '&file_uid=' + fileUid,
            );
            thunkApi.dispatch(mediaFileSuccess(imageMetadata));
            return imageMetadata;
        } catch (e: any) {
            Tracker.trackError(e, ErrorTags.GET_FILE_METADATA_ERROR, {
                orgUid: orgUid,
                fileUid: fileUid,
            });

            thunkApi.dispatch(mediaFileError(e));
            throw {failure: 'true'};
        }
    },
);

const getFileDataFromUrl = createAsyncThunk('media/file', async ({fileUrl}: {fileUrl: string}, thunkApi) => {
    try {
        thunkApi.dispatch(mediaListFilesInit());
        const imageMetadata = await urlOperation(fileUrl);
        thunkApi.dispatch(mediaFileSuccess(imageMetadata));
        return imageMetadata;
    } catch (e: any) {
        thunkApi.dispatch(mediaFileError(e));
        throw {failure: 'true'};
    }
});

const clearFileState = createAsyncThunk('media/clearFiles', async ({fileUid}: {fileUid?: string}, thunkApi) => {
    try {
        thunkApi.dispatch(mediaFileClear());
    } catch (e: any) {
        throw {failure: 'true'};
    }
});

export const clearFileUploadState = createAsyncThunk(
    'media/clearFileUpload',
    async ({fileUid}: {fileUid?: string}, thunkApi) => {
        try {
            thunkApi.dispatch(mediaFileListClear());
        } catch (e: any) {
            throw {failure: 'true'};
        }
    },
);

const getBackgroundFiles = createAsyncThunk('media/getBackgroundFiles', async (orgUid: string, thunkApi) => {
    try {
        const imageMetadata = await get(SWFT_DIGITAL_API_HOST + `/media/background_files/?org_uid=${orgUid}`);
        thunkApi.dispatch(mediaSampleFetchSuccess(imageMetadata));
        return imageMetadata;
    } catch (e: any) {
        Tracker.trackError(e, ErrorTags.GET_SAMPLE_BACKGROUND_FILE_ERROR, {
            orgUid: orgUid,
        });
        thunkApi.dispatch(mediaSampleFileError(e));
        throw {failure: 'true'};
    }
});

const getUnsplashPhotos = createAsyncThunk(
    'media/getUnsplashPhotos',
    async ({query, limit, page}: {query: string; limit: number; page: number}, thunkApi) => {
        try {
            const imageMetadata = await get(
                SWFT_DIGITAL_API_HOST + `/media/unsplash/search?q=${query}&limit=${limit}&page=${page}`,
            );
            thunkApi.dispatch(mediaUnsplashSuccess(imageMetadata));
            return imageMetadata;
        } catch (e: any) {
            Tracker.trackError(e, ErrorTags.GET_UNSPLASH_IMAGES_ERROR);
            thunkApi.dispatch(mediaUnsplashError(e));
            throw {failure: 'true'};
        }
    },
);

const unsplashTracker = createAsyncThunk('media/unsplashTracker', async ({image_id}: {image_id: string}, thunkApi) => {
    try {
        const body = {
            action: 'track_download',
            image_id: image_id,
        };
        return await post(SWFT_DIGITAL_API_HOST + `/media/unsplash`, body);
    } catch (e: any) {
        Tracker.trackError(e, ErrorTags.UNSPLASH_TRACKING_ERROR);
        thunkApi.dispatch(mediaUnsplashError(e));
        throw {failure: 'true'};
    }
});

const editorTableData = createAsyncThunk(
    'media/unsplashTracker',
    async ({tableData, offset, pageCount}: {tableData: MediaFile[]; offset: number; pageCount: number}, thunkApi) => {
        try {
            thunkApi.dispatch(
                mediaAllPaginatedFiles({
                    tablePaginatedData: tableData,
                    offset: offset,
                    pageCount: pageCount,
                }),
            );
        } catch (e: any) {
            throw {failure: 'true'};
        }
    },
);

export {
    uploadFile,
    getMediaFiles,
    deleteMediaFile,
    deleteImageMediaFile,
    saveImageMediaFile,
    getFileMetaData,
    clearFileState,
    getBackgroundFiles,
    searchMediaFiles,
    getUnsplashPhotos,
    unsplashTracker,
    getFileDataFromUrl,
    editorTableData,
};
